import React, { useState } from "react";
import { 
  Modal, Box, Typography, Stack, TextField, 
  Button, IconButton, CircularProgress, alpha 
} from "@mui/material";
import { Close as CloseIcon, Save as SaveIcon, ErrorOutline, DeleteSweep as ClearIcon } from "@mui/icons-material";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import { getFlashMessage, updateFlashMessage } from "../../../api/services/Client/medicalConditions";
import { QK } from "../../../base/qk";

const FlashMessageModal = ({ client, onClose }) => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({ flash_message: "", display_until: "" });

  const { isLoading: isFetching } = useQuery(
    ["flashMessage", client?.id],
    () => getFlashMessage(client.id),
    {
      enabled: !!client,
      onSuccess: (data) => setFormData({
        flash_message: data.flash_message || "",
        display_until: data.display_until || ""
      }),
    }
  );

  const { mutate: saveFlash, isLoading: isSaving } = useMutation(updateFlashMessage, {
    onSuccess: () => {
      enqueueSnackbar("Changes applied successfully", { variant: "success" });
      queryClient.invalidateQueries(QK.clients);
      onClose();
    },
    onError: (error) => {
      enqueueSnackbar("Failed to update system", { variant: "error" });
    }
  });

  const handleSave = () => {
    const payload = {
      flash_message: formData.flash_message,
      display_until: formData.display_until === "" ? null : formData.display_until
    };
    saveFlash({ client_id: client.id, data: payload });
  };

  // Local reset: only clears the form fields, does NOT call the API.
  const handleLocalClear = () => {
    setFormData({ flash_message: "", display_until: "" });
  };

  return (
    <Modal 
      open={!!client} 
      onClose={() => !isSaving && onClose()}
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}
    >
      <Box sx={{
        width: { xs: '100%', sm: 450 }, maxHeight: '90vh',
        bgcolor: 'background.paper', borderRadius: 4, p: 4, outline: 'none', overflowY: 'auto'
      }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <ErrorOutline sx={{ color: '#f43f5e', fontSize: 28 }} />
            <Typography variant="h6" fontWeight={900} color="#1e293b">Critical Alert</Typography>
          </Stack>
          <IconButton size="small" onClick={onClose} disabled={isSaving} sx={{ bgcolor: '#f1f5f9' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>

        {isFetching ? (
          <Box display="flex" justifyContent="center" py={6}><CircularProgress size={32} sx={{ color: '#f43f5e' }} /></Box>
        ) : (
          <Stack spacing={3}>
            <Box>
              <Typography variant="caption" fontWeight={800} color="text.secondary" sx={{ ml: 1, mb: 0.5, display: 'block' }}>
                ALERT MESSAGE
              </Typography>
              <TextField
                fullWidth multiline minRows={4} maxRows={15}
                placeholder="Enter urgent clinical notes..."
                value={formData.flash_message}
                onChange={(e) => setFormData({ ...formData, flash_message: e.target.value })}
                variant="filled"
                InputProps={{ disableUnderline: true, sx: { borderRadius: "12px", fontWeight: 600 } }}
              />
            </Box>

            <Box>
              <Typography variant="caption" fontWeight={800} color="text.secondary" sx={{ ml: 1, mb: 0.5, display: 'block' }}>
                EXPIRY DATE (OPTIONAL)
              </Typography>
              <TextField
                fullWidth type="date" variant="filled"
                value={formData.display_until || ""}
                onChange={(e) => setFormData({ ...formData, display_until: e.target.value })}
                InputProps={{ 
                  disableUnderline: true, 
                  sx: { borderRadius: "12px" },
                  endAdornment: formData.display_until && (
                    <IconButton size="small" onClick={() => setFormData({...formData, display_until: ""})} sx={{ mr: 1 }}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  )
                }}
              />
            </Box>

            <Stack direction="row" spacing={2}>
              <Button
                fullWidth variant="outlined" color="error"
                startIcon={<ClearIcon />}
                onClick={handleLocalClear}
                disabled={isSaving}
                sx={{ borderRadius: "12px", py: 1.5, fontWeight: 800, textTransform: 'none' }}
              >
                Clear
              </Button>
              <Button
                fullWidth variant="contained"
                startIcon={isSaving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                onClick={handleSave}
                disabled={isSaving}
                sx={{ 
                  bgcolor: '#f43f5e', py: 1.5, borderRadius: "12px", fontWeight: 900, textTransform: 'none',
                  '&:hover': { bgcolor: '#e11d48' }
                }}
              >
                Save Changes
              </Button>
            </Stack>
          </Stack>
        )}
      </Box>
    </Modal>
  );
};

export default FlashMessageModal;